import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, InputGroup } from "react-bootstrap";
import ImageWithLoader from "shared/ui/image";
import { createProductDiscount, cancelProductDiscount } from "../api/discountsApi";
import { toast } from "react-toastify";

export default function DiscountProductModal({
  show,
  handleClose,
  product = {},
  onUpdated,
}) {
  const price = Number(product?.price ?? 0);

  const [discountPrice, setDiscountPrice] = useState("");
  const [discountPercent, setDiscountPercent] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  const [priceError, setPriceError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (show) {
      setDiscountPrice(product?.discountPrice ?? "");
      setDiscountPercent(product?.discountPercent ?? "");
      setPassword("");
      setPriceError("");
    }
  }, [show, product?.id]);

  // ===============================
  // 🟦 Хямдарсан үнэ бичих
  // ===============================
  const handleChangePrice = (e) => {
    const val = e.target.value.replace(/,/g, "");
    const num = val === "" ? "" : Number(val);

    if (num >= price) {
      setPriceError("Хямдарсан үнэ нь барааны үнээс бага байх ёстой");
      setDiscountPrice(num);
      setDiscountPercent("");
      return;
    } else {
      setPriceError("");
    }

    setDiscountPrice(num);

    if (num !== "" && price > 0) {
      const pct = Math.round(((price - num) / price) * 100);

      if (pct < 1) {
        setPriceError("Хямдралын хувь нь 1-ээс их байх ёстой");
        setDiscountPercent("");
        return;
      }

      setDiscountPercent(pct);
    } else {
      setDiscountPercent("");
    }
  };

  // 🟩 Хадгалах payload
  const buildSavePayload = () => ({
    productId: product.id,
    discountType: "FIXED",
    discountValue: Number(discountPrice),
    startAt: null,
    endAt: null,
    isActive: 1,
    password: password,
  });

  // ❌ Цуцлах payload — Таны ХҮССЭН яг ийм 3 талбар
  const buildCancelPayload = () => ({
    productId: product.id,
    isActive: 0,
    password: password,
  });

  // =============================
  // 🟥 Хямдрал хадгалах
  // =============================
  const handleSave = async () => {
    if (!password) return toast.error("Нууц үгээ оруулна уу.");
    if (!product?.id) return toast.error("Барааны ID олдсонгүй.");
    if (priceError) return;
    // ⚠️ Хоосон/0 хямдарсан үнэ хадгалахаас сэргийлэх — өмнө нь discountValue=0
    //    (100% хямдрал) чимээгүй хадгалагддаг байв.
    const dp = Number(discountPrice);
    if (discountPrice === "" || Number.isNaN(dp) || dp <= 0) {
      return toast.error("Хямдарсан үнээ зөв оруулна уу.");
    }
    if (dp >= price) {
      return toast.error("Хямдарсан үнэ нь барааны үнээс бага байх ёстой.");
    }

    try {
      setSaving(true);
      const res = await createProductDiscount(buildSavePayload());

      if (res?.success) {
        toast.success(res?.message || "Хямдрал амжилттай хадгалагдлаа.");

        onUpdated?.({
          ...product,
          discountPrice,
          discountPercent,
          hasDiscount: true,
        });

        handleClose();
      } else {
        toast.error(res?.message || "Хадгалж чадсангүй.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Серверийн алдаа.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // =============================
  // ❌ Хямдрал ЦУЦЛАХ — ШИНЭ API ашиглана
  // =============================
  const handleCancelDiscount = async () => {
    if (!password) return toast.error("Нууц үгээ оруулна уу.");
    if (!product?.id) return toast.error("Барааны ID олдсонгүй.");

    try {
      setSaving(true);
      const res = await cancelProductDiscount(buildCancelPayload());

      if (res?.success) {
        toast.success(res?.message || "Хямдрал амжилттай цуцлагдлаа.");

        onUpdated?.({
          ...product,
          discountPrice: null,
          discountPercent: null,
          hasDiscount: false,
        });

        handleClose();
      } else {
        toast.error(res?.message || "Хямдрал цуцлахад алдаа гарлаа.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Серверийн алдаа.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Хямдруулах</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Барааны нэр</Form.Label>
            <Form.Control value={product?.name || ""} readOnly />
          </Form.Group>

          <Row className="mb-3">
            <Col xs={12} className="d-flex align-items-center">
              <ImageWithLoader
                src={product?.image}
                alt={product?.name}
                width="60px"
                height="60px"
                className="me-3 rounded"
              />
              <div className="text-muted">
                Барааны үнэ: {price.toLocaleString()}₮
              </div>
            </Col>
          </Row>

          {/* Хямдарсан үнэ + хувь */}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Хямдарсан үнэ</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  value={discountPrice === "" ? "" : discountPrice}
                  onChange={handleChangePrice}
                  isInvalid={!!priceError}
                />
                {priceError && (
                  <Form.Control.Feedback type="invalid">
                    {priceError}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Хямдралын хувь</Form.Label>
                <Form.Control
                  type="text"
                  value={
                    discountPercent === "" ? "" : `${discountPercent}%`
                  }
                  readOnly
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Нууц үг */}
          <Form.Group className="mb-3">
            <Form.Label>Нууц үг</Form.Label>
            <InputGroup>
              <Form.Control
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Хадгалах / цуцлахын тулд нууц үгээ оруулна уу"
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowPw(!showPw)}
              >
                {showPw ? (
                  <i className="ri ri-eye-off-line" />
                ) : (
                  <i className="ri ri-eye-line" />
                )}
              </Button>
            </InputGroup>
          </Form.Group>

          {/* ❌ Хямдрал цуцлах товч */}
          <div className="d-flex justify-content-end">
            <Button
              variant="outline-danger"
              className="me-2"
              onClick={handleCancelDiscount}
              disabled={saving}
            >
              Хямдралыг цуцлах
            </Button>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Хаах
        </Button>
        <Button
          variant="danger"
          onClick={handleSave}
          disabled={saving || !!priceError}
        >
          {saving ? "Хадгалж байна..." : "Хадгалах"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
